import { VH_MESSAGES, createVHAdapter, notifyParent, onParentMessage } from '@cv-builder/adapter-vh'
import {
  CVBuilder,
  DEFAULT_CV_LOCALE,
  I18nProvider,
  type LocalizationConfig,
  PlatformProvider,
  type User,
  useAuth,
  useCV,
  useI18n,
} from '@cv-builder/core'
import { useEffect, useState } from 'react'
import vhLogo from '../../../assets/virtual-hive-logo/192.png'

// Get API URL from environment or default
const apiBaseUrl = import.meta.env.VITE_VH_API_URL || '/api'
const initialLocaleConfig = readLocaleConfigFromUrl()
// Hive context for the `virtualhive-tenant` header. The client-mt host passes it
// on the iframe URL (?hiveId=...); VITE_HIVE_ID is the local-dev fallback.
const hiveParams = new URLSearchParams(window.location.search)
const hiveId = hiveParams.get('hiveId')?.trim() || import.meta.env.VITE_HIVE_ID || null

// Branding from the embedding hive (client-mt passes these on the iframe URL):
// the hive's name becomes the header title, and its theme colors retarget the
// top bar. The Virtual Hive logo replaces the default "VH" mark.
const hiveName = hiveParams.get('hiveName')?.trim() || undefined
const themeColor = hiveParams.get('themeColor')?.trim() || undefined
const themeTextColor = hiveParams.get('themeText')?.trim() || undefined
const branding = {
  logo: vhLogo,
  logoAlt: 'Virtual Hive',
  ...(hiveName ? { title: hiveName } : {}),
  ...(themeColor ? { topbarColor: themeColor, primaryColor: themeColor } : {}),
  ...(themeTextColor ? { topbarTextColor: themeTextColor } : {}),
}

// Create the Virtual Hive adapter
const adapter = createVHAdapter({
  apiBaseUrl,
  getHiveId: () => hiveId,
  auth: {
    timeout: 10000,
  },
  config: {
    locale: initialLocaleConfig,
    branding,
  },
})

function readLocaleConfigFromUrl(): Partial<LocalizationConfig> {
  const params = new URLSearchParams(window.location.search)
  const sharedLocale = params.get('locale')?.trim()
  const uiLocale = params.get('uiLocale')?.trim() || sharedLocale
  const cvLocale = params.get('cvLocale')?.trim() || sharedLocale
  const supportedLocales = params
    .get('supportedLocales')
    ?.split(',')
    .map((locale) => locale.trim())
    .filter(Boolean)

  return compactLocaleConfig({
    defaultUiLocale: uiLocale,
    defaultCvLocale: cvLocale,
    supportedLocales,
  })
}

function readLocaleConfigFromPayload(payload: unknown): Partial<LocalizationConfig> {
  if (typeof payload === 'string') {
    return { defaultUiLocale: payload, defaultCvLocale: payload }
  }

  if (!payload || typeof payload !== 'object') {
    return {}
  }

  const data = payload as Record<string, unknown>
  const sharedLocale = readString(data.locale)
  const supportedLocales = Array.isArray(data.supportedLocales)
    ? data.supportedLocales.filter((locale): locale is string => typeof locale === 'string')
    : undefined

  return compactLocaleConfig({
    defaultUiLocale: readString(data.uiLocale) ?? sharedLocale,
    defaultCvLocale: readString(data.cvLocale) ?? sharedLocale,
    supportedLocales,
  })
}

function compactLocaleConfig(config: Partial<LocalizationConfig>): Partial<LocalizationConfig> {
  const nextConfig: Partial<LocalizationConfig> = {}

  if (config.defaultUiLocale) nextConfig.defaultUiLocale = config.defaultUiLocale
  if (config.defaultCvLocale) nextConfig.defaultCvLocale = config.defaultCvLocale
  if (config.supportedLocales?.length) nextConfig.supportedLocales = config.supportedLocales

  return nextConfig
}

function readString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined
}

function CVBuilderApp() {
  const { cv, isLoading, error, updateCV, createCV, loadCV } = useCV()
  const auth = useAuth()
  // Auth resolves ASYNCHRONOUSLY over postMessage (parent pushes AUTH_STATE_CHANGED
  // and/or answers REQUEST_USER_INFO). The gate must react to that — reading
  // isAuthenticated() at render time only works if the token happens to arrive
  // before React's first commit (true in Chrome, NOT in Firefox, which delivers
  // the message a tick later, leaving the "Godkender..." screen stuck forever).
  // onAuthStateChange registers a listener AND proactively pulls user info, so
  // whichever path resolves first flips this state and re-renders the gate.
  const [authUser, setAuthUser] = useState<User | null>(auth.getUser())
  useEffect(() => auth.onAuthStateChange(setAuthUser), [auth])
  const { setLocale, t } = useI18n()
  const [defaultCvLocale, setDefaultCvLocale] = useState(
    adapter.config.locale?.defaultCvLocale ?? DEFAULT_CV_LOCALE
  )
  const [pendingCvLocale, setPendingCvLocale] = useState<string | null>(null)
  const [cvId, setCvId] = useState<string | null>(null)

  useEffect(() => {
    return onParentMessage((message) => {
      if (message.type !== VH_MESSAGES.SET_LOCALE) return

      const localeConfig = readLocaleConfigFromPayload(message.payload)

      if (localeConfig.defaultUiLocale) {
        setLocale(localeConfig.defaultUiLocale)
      }

      if (localeConfig.defaultCvLocale) {
        setDefaultCvLocale(localeConfig.defaultCvLocale)
        if (cv) {
          void updateCV({ locale: localeConfig.defaultCvLocale })
        } else {
          setPendingCvLocale(localeConfig.defaultCvLocale)
        }
      }
    })
  }, [cv, setLocale, updateCV])

  useEffect(() => {
    if (!cv || !pendingCvLocale) return

    if (cv.locale !== pendingCvLocale) {
      void updateCV({ locale: pendingCvLocale })
    }
    setPendingCvLocale(null)
  }, [cv, pendingCvLocale, updateCV])

  // Get CV ID from URL params (set by parent window)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const id = params.get('cvId')
    if (id) {
      setCvId(id)
      loadCV(id)
    }
  }, [loadCV])

  // Handle creating a new CV if no ID provided
  useEffect(() => {
    if (!cvId && authUser) {
      const initCV = async () => {
        try {
          const cvs = await adapter.storage.listCVs()
          if (cvs.length > 0 && cvs[0]) {
            setCvId(cvs[0].id)
            await loadCV(cvs[0].id)
          } else {
            const newCV = await createCV({
              title: t('app.defaultCvTitle'),
              locale: defaultCvLocale,
            })
            setCvId(newCV.id)
            // Notify parent of the new CV
            notifyParent(VH_MESSAGES.CV_SAVED, { cvId: newCV.id })
          }
        } catch (err) {
          console.error('Failed to initialize CV:', err)
          notifyParent(VH_MESSAGES.ERROR, { message: t('app.failedToInitializeCv') })
        }
      }
      initCV()
    }
  }, [cvId, authUser, createCV, defaultCvLocale, loadCV, t])

  if (!authUser) {
    return (
      <div className="loading">
        <p>{t('app.authenticating')}</p>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="loading">
        <p>{t('app.loadingCv')}</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="error">
        <p>
          {t('app.error')}: {error.message}
        </p>
        <button type="button" onClick={() => window.location.reload()}>
          {t('actions.retry')}
        </button>
      </div>
    )
  }

  if (!cv) {
    return (
      <div className="loading">
        <p>{t('app.preparingCv')}</p>
      </div>
    )
  }

  const handleChange = async (data: Parameters<typeof updateCV>[0]) => {
    await updateCV(data)
    notifyParent(VH_MESSAGES.CV_SAVED, { cvId: cv.id })
    if (data.locale) {
      notifyParent(VH_MESSAGES.LOCALE_CHANGED, { cvId: cv.id, cvLocale: data.locale })
    }
  }

  return <CVBuilder cv={cv} onChange={handleChange} />
}

export function App() {
  const localeConfig = adapter.config.locale

  return (
    <I18nProvider
      locale={localeConfig?.defaultUiLocale}
      supportedLocales={localeConfig?.supportedLocales}
      onLocaleChange={(locale) => notifyParent(VH_MESSAGES.LOCALE_CHANGED, { uiLocale: locale })}
    >
      <PlatformProvider adapter={adapter}>
        <CVBuilderApp />
      </PlatformProvider>
    </I18nProvider>
  )
}
